import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Users as UsersIcon, Receipt, Euro, TrendingUp, UserCog, LogIn, Trash2, RefreshCw, AlertTriangle, RotateCcw, Eye, X, Filter, ArrowUp, ArrowDown, KeyRound, Gift, PlusCircle } from "lucide-react";
import api, { formatApiError, setToken, getToken } from "@/lib/api";
import { formatDateShort, formatDateTimeShort } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import SubscriptionBillingSection from "@/components/admin/SubscriptionBillingSection";

const PLAN_OPTIONS = ["free", "basic", "pro", "business"];
const ROLE_OPTIONS = ["user", "admin", "master"];
const SORT_OPTIONS = ["name-asc", "name-desc", "invoices-desc", "invoices-asc", "month-desc"];

function planLabel(p) {
  return ({ free: "Free", basic: "Basic", pro: "Pro", business: "Business" })[p] || p;
}

function roleBadge(role) {
  const cls = {
    master: "bg-[var(--brand-accent)]/20 text-[var(--brand-accent)]",
    admin: "bg-[#7A5C44]/15 text-[#7A5C44]",
    user: "bg-[var(--surface-hover)] text-[var(--text-secondary)]",
  }[role] || "bg-[var(--surface-hover)] text-[var(--text-secondary)]";
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {role}
    </span>
  );
}

export default function Admin() {
  const { user, refresh } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Multi-selection + filters + sort
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [minInvoices, setMinInvoices] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [bulkPlanOpen, setBulkPlanOpen] = useState(false);
  const [bulkPlanChoice, setBulkPlanChoice] = useState("free");
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, sm] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: q ? { q } : {} }),
        api.get("/admin/signups-by-month", { params: { months: 12 } }),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setSignups(sm.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter + sort. `users` is already filtered by `q` on the
  // server; here we further narrow by plan/role/min-invoices and reorder.
  const visibleUsers = useMemo(() => {
    const min = parseInt(minInvoices, 10);
    const hasMin = !isNaN(min);
    let arr = users.filter((u) => {
      if (filterPlan !== "all" && u.plan !== filterPlan) return false;
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (hasMin && (u.invoices_total || 0) < min) return false;
      return true;
    });
    const sorters = {
      "name-asc":       (a, b) => (a.email || "").localeCompare(b.email || ""),
      "name-desc":      (a, b) => (b.email || "").localeCompare(a.email || ""),
      "invoices-desc":  (a, b) => (b.invoices_total || 0) - (a.invoices_total || 0),
      "invoices-asc":   (a, b) => (a.invoices_total || 0) - (b.invoices_total || 0),
      "month-desc":     (a, b) => (b.invoices_this_month || 0) - (a.invoices_this_month || 0),
    };
    arr = [...arr].sort(sorters[sortBy] || sorters["name-asc"]);
    return arr;
  }, [users, filterPlan, filterRole, minInvoices, sortBy]);

  // Prune selections that no longer match the current filter (or that were
  // removed from the underlying list). Keeps the "N seleccionados" accurate.
  useEffect(() => {
    const visibleIds = new Set(visibleUsers.map((u) => u.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleUsers]);

  // Ancla para selección por rango con Shift+click. Guardamos el índice del
  // último toggle "normal" (sin Shift) y aplicamos el mismo estado (marcar/
  // desmarcar) a todo el rango cuando el usuario Shift-clickea otra fila.
  const [rangeAnchorIdx, setRangeAnchorIdx] = useState(null);

  const toggleOne = (id, event, idx) => {
    // Shift+click: aplicar el mismo estado a todo el rango [anchor..idx].
    if (event?.shiftKey && rangeAnchorIdx != null && idx != null && rangeAnchorIdx !== idx) {
      const [from, to] = rangeAnchorIdx < idx ? [rangeAnchorIdx, idx] : [idx, rangeAnchorIdx];
      const anchorId = visibleUsers[rangeAnchorIdx]?.id;
      const shouldSelect = selectedIds.has(anchorId);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) {
          const uid = visibleUsers[i]?.id;
          if (!uid) continue;
          if (shouldSelect) next.add(uid);
          else next.delete(uid);
        }
        return next;
      });
      setRangeAnchorIdx(idx);
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (idx != null) setRangeAnchorIdx(idx);
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const allSelected = visibleUsers.length > 0 && visibleUsers.every((u) => prev.has(u.id));
      if (allSelected) return new Set();
      const next = new Set(prev);
      for (const u of visibleUsers) next.add(u.id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const clearFilters = () => { setFilterPlan("all"); setFilterRole("all"); setMinInvoices(""); setSortBy("name-asc"); };

  const bulkChangePlan = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-plan", {
        user_ids: Array.from(selectedIds),
        plan: bulkPlanChoice,
      });
      toast.success(
        lang === "es"
          ? `${data.modified} usuario(s) movido(s) a ${planLabel(bulkPlanChoice)}`
          : `${data.modified} user(s) moved to ${planLabel(bulkPlanChoice)}`
      );
      setBulkPlanOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", {
        user_ids: Array.from(selectedIds),
      });
      const skipped = (data.skipped_masters || 0) + (data.skipped_self || 0);
      toast.success(
        lang === "es"
          ? `${data.deleted} usuario(s) eliminado(s)${skipped ? ` · ${skipped} omitido(s)` : ""}`
          : `${data.deleted} user(s) deleted${skipped ? ` · ${skipped} skipped` : ""}`
      );
      setBulkDeleteOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success(lang === "es" ? "Usuario actualizado" : "User updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(lang === "es" ? "Usuario eliminado" : "User deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const impersonate = async (id, email) => {
    // Stash the master token so we can either "return to master" later OR
    // roll back if impersonation fails partway (e.g. refresh() 401 after
    // setToken swap). Without a rollback, a failure here would leave the
    // master logged out with a broken impersonation token.
    const masterToken = getToken();
    if (masterToken) localStorage.setItem("impersonator_token", masterToken);
    try {
      const { data } = await api.post(`/admin/users/${id}/impersonate`);
      setToken(data.access_token);
      if (data.audit_session_id) {
        // iter 59 — remember the audit row id so we can close it on exit.
        localStorage.setItem("impersonation_audit_id", data.audit_session_id);
      }
      await refresh();
      toast.success((lang === "es" ? "Entrando como " : "Logged in as ") + email);
      navigate("/dashboard");
    } catch (err) {
      // Restore master session if we swapped the token before failing.
      if (masterToken) setToken(masterToken);
      localStorage.removeItem("impersonator_token");
      toast.error(formatApiError(err));
    }
  };

  // Reset password (master-only). Abre un diálogo con la contraseña nueva a
  // introducir por el master y hace POST /admin/users/{id}/reset-password.
  const [resetPwUser, setResetPwUser] = useState(null); // {id,email} or null
  const [resetPwValue, setResetPwValue] = useState("");
  const [resetPwBusy, setResetPwBusy] = useState(false);
  const submitResetPw = async () => {
    if (!resetPwUser || resetPwValue.length < 6) {
      toast.error(lang === "es" ? "Mínimo 6 caracteres" : "Min 6 characters");
      return;
    }
    setResetPwBusy(true);
    try {
      await api.post(`/admin/users/${resetPwUser.id}/reset-password`, {
        new_password: resetPwValue,
      });
      toast.success(lang === "es"
        ? `Contraseña actualizada para ${resetPwUser.email}`
        : `Password updated for ${resetPwUser.email}`);
      setResetPwUser(null);
      setResetPwValue("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetPwBusy(false);
    }
  };

  // Iter 63 — Grant free time to a tenant (master-only). Amount is a free
  // form int, backend caps at 180 days / 6 months. Dialog auto-selects the
  // right `kind` based on the user's current plan.
  const [grantUser, setGrantUser] = useState(null); // {id, email, plan}
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");

  // Iter 82.6 — bonus de facturas del mes actual.
  const [invBonusUser, setInvBonusUser] = useState(null);
  const [invBonusAmount, setInvBonusAmount] = useState("50");
  const [invBonusNote, setInvBonusNote] = useState("");
  const [invBonusBusy, setInvBonusBusy] = useState(false);

  const submitInvoiceBonus = async () => {
    if (!invBonusUser) return;
    const n = parseInt(invBonusAmount);
    if (!n || n < 1) {
      toast.error(lang === "es" ? "Cantidad inválida" : "Invalid amount");
      return;
    }
    setInvBonusBusy(true);
    try {
      await api.post(`/admin/users/${invBonusUser.id}/invoice-bonus`, {
        extra_quota: n,
        note: invBonusNote,
      });
      toast.success(
        lang === "es"
          ? `+${n} facturas para ${invBonusUser.email} este mes`
          : `+${n} invoices for ${invBonusUser.email} this month`,
      );
      setInvBonusUser(null);
      setInvBonusAmount("50");
      setInvBonusNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInvBonusBusy(false);
    }
  };
  const [grantBusy, setGrantBusy] = useState(false);
  const submitGrant = async () => {
    if (!grantUser) return;
    const n = parseInt(grantAmount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error(lang === "es" ? "Cantidad inválida" : "Invalid amount");
      return;
    }
    const kind = grantUser.plan === "free" ? "trial_days" : "free_months";
    setGrantBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${grantUser.id}/grant`, {
        kind,
        amount: n,
        reason: grantReason || null,
      });
      toast.success(
        lang === "es"
          ? `Otorgado: ${n} ${kind === "trial_days" ? "días" : "meses"} a ${grantUser.email}`
          : `Granted: ${n} ${kind === "trial_days" ? "days" : "months"} to ${grantUser.email}`,
        { description: data?.trial_ends_at || data?.plan_renews_at }
      );
      setGrantUser(null);
      setGrantAmount("");
      setGrantReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGrantBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Panel maestro" : "Master panel"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Administración" : "Administration"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {lang === "es"
              ? "Gestiona usuarios, planes y métricas SaaS. Acceso restringido a roles master/admin."
              : "Manage users, plans and SaaS metrics. Restricted to master/admin roles."}
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-refresh">
          <RefreshCw size={14} className="mr-1.5" /> {lang === "es" ? "Actualizar" : "Refresh"}
        </Button>
      </div>

      <StatsCards stats={stats} lang={lang} />

      {signups && <SignupsByMonthWidget data={signups} lang={lang} />}

      {isMaster && <ImpersonationAudit lang={lang} />}

      {/* Iter 82.6 — facturación de suscripción */}
      <SubscriptionBillingSection lang={lang} isMaster={isMaster} />

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {lang === "es" ? "Usuarios" : "Users"}{" "}
            <span className="text-sm text-[var(--text-muted)] font-normal">
              ({visibleUsers.length}{visibleUsers.length !== users.length ? ` / ${users.length}` : ""})
            </span>
          </h2>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              data-testid="admin-search"
              placeholder={lang === "es" ? "Buscar email..." : "Search email..."}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>

        {/* Filter + sort row */}
        <div className="flex items-center gap-2 flex-wrap mb-3 pb-3 border-b border-[var(--border-color)]" data-testid="admin-filters">
          <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <Filter size={12} />
            <span className="uppercase tracking-wider">{lang === "es" ? "Filtros" : "Filters"}</span>
          </div>
          <Select value={filterPlan} onValueChange={setFilterPlan}>
            <SelectTrigger className="h-8 w-32" data-testid="admin-filter-plan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === "es" ? "Todos los planes" : "All plans"}</SelectItem>
              {PLAN_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{planLabel(p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="h-8 w-32" data-testid="admin-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === "es" ? "Todos los roles" : "All roles"}</SelectItem>
              {ROLE_OPTIONS.map((r) => (
                <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            min="0"
            step="1"
            value={minInvoices}
            onChange={(e) => setMinInvoices(e.target.value)}
            placeholder={lang === "es" ? "Total ≥" : "Total ≥"}
            className="h-8 w-24 font-mono-num"
            data-testid="admin-filter-min-invoices"
          />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-44" data-testid="admin-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">
                <span className="inline-flex items-center gap-1"><ArrowUp size={10} />{lang === "es" ? "Nombre A → Z" : "Name A → Z"}</span>
              </SelectItem>
              <SelectItem value="name-desc">
                <span className="inline-flex items-center gap-1"><ArrowDown size={10} />{lang === "es" ? "Nombre Z → A" : "Name Z → A"}</span>
              </SelectItem>
              <SelectItem value="invoices-desc">
                <span className="inline-flex items-center gap-1"><ArrowDown size={10} />{lang === "es" ? "Total facturas ↓" : "Total invoices ↓"}</span>
              </SelectItem>
              <SelectItem value="invoices-asc">
                <span className="inline-flex items-center gap-1"><ArrowUp size={10} />{lang === "es" ? "Total facturas ↑" : "Total invoices ↑"}</span>
              </SelectItem>
              <SelectItem value="month-desc">
                <span className="inline-flex items-center gap-1"><ArrowDown size={10} />{lang === "es" ? "Este mes ↓" : "This month ↓"}</span>
              </SelectItem>
            </SelectContent>
          </Select>
          {(filterPlan !== "all" || filterRole !== "all" || minInvoices || sortBy !== "name-asc") && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="admin-clear-filters">
              <X size={12} className="mr-1" />{lang === "es" ? "Limpiar" : "Clear"}
            </Button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 mb-3 rounded-lg bg-[var(--primary)]/5 border border-[var(--primary)]/40" data-testid="admin-bulk-bar">
            <p className="text-sm">
              <span className="font-semibold">{selectedIds.size}</span>{" "}
              {lang === "es"
                ? `usuario${selectedIds.size === 1 ? "" : "s"} seleccionado${selectedIds.size === 1 ? "" : "s"}`
                : `user${selectedIds.size === 1 ? "" : "s"} selected`}
            </p>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setBulkPlanOpen(true)} data-testid="admin-bulk-plan-btn">
                <UserCog size={12} className="mr-1.5" />
                {lang === "es" ? "Cambiar plan" : "Change plan"}
              </Button>
              {isMaster && (
                <Button
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="bg-[var(--status-negative)] hover:bg-[var(--status-negative)]/90 text-white"
                  data-testid="admin-bulk-delete-btn"
                >
                  <Trash2 size={12} className="mr-1.5" />
                  {lang === "es" ? "Eliminar" : "Delete"}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="admin-bulk-clear">
                <X size={12} />
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
              <tr className="border-b border-[var(--border-color)]">
                <th className="w-8 py-2 px-2">
                  <input
                    type="checkbox"
                    checked={visibleUsers.length > 0 && visibleUsers.every((u) => selectedIds.has(u.id))}
                    onChange={toggleAllVisible}
                    data-testid="admin-select-all"
                    className="rounded border-[var(--border-color)]"
                    aria-label={lang === "es" ? "Seleccionar todos" : "Select all"}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Email" : "Email"}</th>
                <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Rol" : "Role"}</th>
                <th className="text-left py-2 px-2 font-medium">Plan</th>
                <th className="text-right py-2 px-2 font-medium">{lang === "es" ? "Este mes" : "This month"}</th>
                <th className="text-right py-2 px-2 font-medium">{lang === "es" ? "Total" : "Total"}</th>
                <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Fecha alta" : "Signed up"}</th>
                <th className="text-right py-2 px-2 font-medium">{lang === "es" ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="8" className="text-center py-8 text-[var(--text-muted)]">…</td></tr>
              )}
              {!loading && visibleUsers.length === 0 && (
                <tr><td colSpan="8" className="text-center py-8 text-[var(--text-muted)]">{lang === "es" ? "Sin usuarios" : "No users"}</td></tr>
              )}
              {!loading && visibleUsers.map((u, uIdx) => (
                <tr key={u.id} className={`border-b border-[var(--border-color)] hover:bg-[var(--surface-hover)] ${selectedIds.has(u.id) ? "bg-[var(--primary)]/5" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                  <td className="py-2 px-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => {}}
                      onClick={(e) => toggleOne(u.id, e, uIdx)}
                      data-testid={`admin-select-${u.id}`}
                      className="rounded border-[var(--border-color)]"
                      aria-label={`Select ${u.email}`}
                      title={lang === "es" ? "Shift+click para seleccionar rango" : "Shift+click to select range"}
                    />
                  </td>
                  <td className="py-2 px-2">
                    <div className="font-medium">{u.email}</div>
                    {u.name && <div className="text-xs text-[var(--text-muted)]">{u.name}</div>}
                  </td>
                  <td className="py-2 px-2">{roleBadge(u.role)}</td>
                  <td className="py-2 px-2">
                    <Select
                      value={u.plan}
                      onValueChange={(v) => updateUser(u.id, { plan: v })}
                    >
                      <SelectTrigger className="h-8 w-28" data-testid={`admin-plan-${u.id}`}>
                        <SelectValue>{planLabel(u.plan)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {PLAN_OPTIONS.map((p) => (
                          <SelectItem key={p} value={p}>{planLabel(p)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 px-2 text-right font-mono-num">
                    {u.invoices_this_month}{u.monthly_limit != null ? ` / ${u.monthly_limit}` : ""}
                  </td>
                  <td className="py-2 px-2 text-right font-mono-num">{u.invoices_total}</td>
                  <td className="py-2 px-2 font-mono-num text-xs text-[var(--text-muted)]" data-testid={`admin-user-created-${u.id}`}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-US") : "—"}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {isMaster && u.id !== user?.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => impersonate(u.id, u.email)}
                          data-testid={`admin-impersonate-${u.id}`}
                          title={lang === "es" ? "Entrar como este usuario" : "Log in as this user"}
                        >
                          <LogIn size={12} />
                        </Button>
                      )}
                      {isMaster && u.id !== user?.id && u.role !== "master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setResetPwUser({ id: u.id, email: u.email }); setResetPwValue(""); }}
                          data-testid={`admin-reset-pw-${u.id}`}
                          title={lang === "es" ? "Cambiar contraseña" : "Reset password"}
                        >
                          <KeyRound size={12} />
                        </Button>
                      )}
                      {isMaster && u.id !== user?.id && u.role !== "master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setGrantUser({ id: u.id, email: u.email, plan: u.plan });
                            setGrantAmount("");
                            setGrantReason("");
                          }}
                          data-testid={`admin-grant-${u.id}`}
                          title={lang === "es"
                            ? (u.plan === "free" ? "Regalar días de trial" : "Regalar meses gratis")
                            : (u.plan === "free" ? "Grant trial days" : "Grant free months")}
                          className="text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/10"
                        >
                          <Gift size={12} />
                        </Button>
                      )}
                      {(isMaster || user?.role === "admin") && u.role !== "master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setInvBonusUser({ id: u.id, email: u.email });
                            setInvBonusAmount("50");
                            setInvBonusNote("");
                          }}
                          data-testid={`admin-invoice-bonus-${u.id}`}
                          title={lang === "es" ? "Añadir facturas extra este mes" : "Add extra invoices this month"}
                          className="text-[var(--primary)] hover:bg-[var(--primary)]/10"
                        >
                          <PlusCircle size={12} />
                        </Button>
                      )}
                      {isMaster && u.id !== user?.id && u.role !== "master" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete(u)}
                          data-testid={`admin-delete-${u.id}`}
                          className="text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
                          title={lang === "es" ? "Eliminar usuario" : "Delete user"}
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OcrFailuresPanel lang={lang} />

      {/* Bulk change plan dialog */}
      <Dialog open={bulkPlanOpen} onOpenChange={(o) => !bulkBusy && setBulkPlanOpen(o)}>
        <DialogContent data-testid="admin-bulk-plan-dialog">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Cambiar plan en bloque" : "Bulk change plan"}</DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? `Aplica el nuevo plan a ${selectedIds.size} usuario(s). Las cuentas master se ignoran.`
                : `Apply the new plan to ${selectedIds.size} user(s). Master accounts are skipped.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Nuevo plan" : "New plan"}
            </label>
            <Select value={bulkPlanChoice} onValueChange={setBulkPlanChoice}>
              <SelectTrigger className="h-9" data-testid="admin-bulk-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p}>{planLabel(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPlanOpen(false)} disabled={bulkBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={bulkChangePlan}
              disabled={bulkBusy}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="admin-bulk-plan-confirm"
            >
              {bulkBusy ? "…" : (lang === "es" ? `Aplicar a ${selectedIds.size}` : `Apply to ${selectedIds.size}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => !bulkBusy && setBulkDeleteOpen(o)}>
        <DialogContent data-testid="admin-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Eliminar usuarios seleccionados" : "Delete selected users"}</DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? `Vas a eliminar ${selectedIds.size} usuario(s) junto con TODAS sus facturas, albaranes, categorías, salarios, escandallos y archivos. Las cuentas master y tu propia cuenta se omitirán. Esta acción no se puede deshacer.`
                : `You are about to delete ${selectedIds.size} user(s) with ALL of their invoices, delivery notes, categories, salaries, dishes and files. Master accounts and your own account will be skipped. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 p-3 rounded bg-[var(--status-negative)]/10 border border-[var(--status-negative)]/30 text-xs text-[var(--status-negative)]">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>
              {lang === "es"
                ? "Confirmación destructiva. Escribe rápido y con conciencia."
                : "Destructive confirmation. Proceed carefully."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="bg-[var(--status-negative)] hover:bg-[var(--status-negative)]/90 text-white"
              data-testid="admin-bulk-delete-confirm"
            >
              <Trash2 size={14} className="mr-1.5" />
              {bulkBusy ? "…" : (lang === "es" ? `Eliminar ${selectedIds.size}` : `Delete ${selectedIds.size}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="admin-confirm-delete">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Eliminar usuario" : "Delete user"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-secondary)]">
            {lang === "es"
              ? `Esto eliminará permanentemente ${confirmDelete?.email} y todas sus facturas, categorías y archivos. Esta acción no se puede deshacer.`
              : `This will permanently remove ${confirmDelete?.email} and all of their invoices, categories and files. This cannot be undone.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              data-testid="admin-confirm-delete-btn"
              className="bg-[var(--status-negative)] hover:bg-[var(--status-negative)]/90 text-white"
              onClick={() => deleteUser(confirmDelete.id)}
            >
              <Trash2 size={14} className="mr-1.5" /> {lang === "es" ? "Eliminar" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog (master-only). */}
      <Dialog open={!!resetPwUser} onOpenChange={(o) => !resetPwBusy && !o && setResetPwUser(null)}>
        <DialogContent data-testid="admin-reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>
              <KeyRound size={16} className="inline mr-1.5 -mt-0.5" />
              {lang === "es" ? "Cambiar contraseña" : "Reset password"}
            </DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? `Estableces una nueva contraseña para ${resetPwUser?.email}. El usuario podrá iniciar sesión con ella inmediatamente.`
                : `Set a new password for ${resetPwUser?.email}. They can log in with it immediately.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              {lang === "es" ? "Nueva contraseña" : "New password"}
            </label>
            <Input
              type="text"
              value={resetPwValue}
              onChange={(e) => setResetPwValue(e.target.value)}
              placeholder={lang === "es" ? "Mínimo 6 caracteres" : "Min 6 characters"}
              data-testid="admin-reset-pw-input"
              autoFocus
            />
            <p className="text-[10px] text-[var(--text-muted)]">
              {lang === "es"
                ? "Comunica la nueva contraseña al usuario por un canal seguro (nunca por email en claro)."
                : "Share the new password with the user through a secure channel (never plain email)."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwUser(null)} disabled={resetPwBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitResetPw}
              disabled={resetPwBusy || resetPwValue.length < 6}
              data-testid="admin-reset-pw-confirm"
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {resetPwBusy ? "..." : (lang === "es" ? "Actualizar" : "Update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 63 — Grant free time dialog (master-only). */}
      <Dialog open={!!grantUser} onOpenChange={(o) => !grantBusy && !o && setGrantUser(null)}>
        <DialogContent data-testid="admin-grant-dialog">
          <DialogHeader>
            <DialogTitle>
              <Gift size={16} className="inline mr-1.5 -mt-0.5 text-[var(--brand-accent)]" />
              {grantUser?.plan === "free"
                ? (lang === "es" ? "Regalar días de trial" : "Grant trial days")
                : (lang === "es" ? "Regalar meses gratis" : "Grant free months")}
            </DialogTitle>
            <DialogDescription>
              {grantUser?.plan === "free"
                ? (lang === "es"
                    ? `${grantUser?.email} está en Free. Se le extenderá la prueba (plan Business) durante los días que indiques. Máximo 180 días por regalo.`
                    : `${grantUser?.email} is on Free. Their Business trial will be extended by the given number of days. Max 180 per gift.`)
                : (lang === "es"
                    ? `${grantUser?.email} está en ${grantUser?.plan}. Se extenderá su fecha de renovación N meses (no se le cobra durante ese tiempo). Máximo 6 meses por regalo.`
                    : `${grantUser?.email} is on ${grantUser?.plan}. Their renewal date will be pushed N months (no charges during that period). Max 6 per gift.`)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)] block mb-1">
                {grantUser?.plan === "free"
                  ? (lang === "es" ? "Días" : "Days")
                  : (lang === "es" ? "Meses" : "Months")}
              </label>
              <Input
                type="number"
                min="1"
                max={grantUser?.plan === "free" ? 180 : 6}
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                placeholder={grantUser?.plan === "free" ? "30" : "1"}
                data-testid="admin-grant-amount"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)] block mb-1">
                {lang === "es" ? "Motivo (opcional)" : "Reason (optional)"}
              </label>
              <Input
                type="text"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder={lang === "es" ? "p.ej. beta tester" : "e.g. beta tester"}
                data-testid="admin-grant-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantUser(null)} disabled={grantBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitGrant}
              disabled={grantBusy || !grantAmount}
              data-testid="admin-grant-confirm"
              className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white"
            >
              <Gift size={12} className="mr-1.5" />
              {grantBusy ? "..." : (lang === "es" ? "Regalar" : "Grant")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 82.6 — bonus de facturas del mes actual */}
      <Dialog open={!!invBonusUser} onOpenChange={(o) => !invBonusBusy && !o && setInvBonusUser(null)}>
        <DialogContent data-testid="admin-invoice-bonus-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle size={18} className="text-[var(--primary)]" />
              {lang === "es" ? "Añadir facturas extra este mes" : "Add extra invoices this month"}
            </DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? `${invBonusUser?.email} — se suma al cupo del plan actual, solo para el mes en curso.`
                : `${invBonusUser?.email} — added to the current plan cap, this month only.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[var(--text-secondary)]">
                {lang === "es" ? "Facturas extra" : "Extra invoices"}
              </Label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={invBonusAmount}
                onChange={(e) => setInvBonusAmount(e.target.value)}
                data-testid="admin-invoice-bonus-amount"
                className="mt-1 w-32 font-mono-num"
              />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-secondary)]">
                {lang === "es" ? "Motivo (opcional)" : "Reason (optional)"}
              </Label>
              <Input
                value={invBonusNote}
                onChange={(e) => setInvBonusNote(e.target.value)}
                data-testid="admin-invoice-bonus-note"
                className="mt-1 text-sm"
                placeholder={lang === "es" ? "p.ej. compensación downtime" : "e.g. downtime compensation"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvBonusUser(null)} disabled={invBonusBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitInvoiceBonus}
              disabled={invBonusBusy}
              data-testid="admin-invoice-bonus-confirm"
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              <PlusCircle size={12} className="mr-1.5" />
              {invBonusBusy ? "..." : (lang === "es" ? "Añadir" : "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SignupsByMonthWidget({ data, lang }) {
  const months = data?.months || [];
  const max = Math.max(1, ...months.map((m) => m.count));
  const total = data?.total || 0;
  const referralTotals = data?.referral_totals || {};
  const SRC_LABELS_ES = {
    google: "Google", friend: "Amigo", colleague: "Compañero (hostelería)",
    social: "Redes sociales", linkedin: "LinkedIn", blog: "Blog", ad: "Anuncio",
    event: "Feria/Evento", other: "Otro", unknown: "Sin especificar",
  };
  const SRC_LABELS_EN = {
    google: "Google", friend: "Friend", colleague: "Hospitality peer",
    social: "Social media", linkedin: "LinkedIn", blog: "Blog", ad: "Advertising",
    event: "Trade fair/Event", other: "Other", unknown: "Unspecified",
  };
  const labels = lang === "es" ? SRC_LABELS_ES : SRC_LABELS_EN;

  // Iter 82 — click en una barra ⇒ carga y muestra el listado de tenants
  // dados de alta ese mes (`/admin/signups-by-month/{month}/tenants`).
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);

  const loadMonthTenants = async (month) => {
    if (selectedMonth === month) {
      // Toggle: click again to hide.
      setSelectedMonth(null);
      setTenants(null);
      return;
    }
    setSelectedMonth(month);
    setLoadingTenants(true);
    setTenants(null);
    try {
      const { data: body } = await api.get(`/admin/signups-by-month/${month}/tenants`);
      setTenants(body.tenants || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4" data-testid="admin-signups-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
            {lang === "es" ? "Altas por mes · últimos 12 meses" : "Signups per month · last 12 months"}
          </p>
          <p className="font-display text-xl font-bold tracking-tight font-mono-num">
            {total} {lang === "es" ? "altas" : "signups"}
          </p>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
            {lang === "es" ? "Haz clic en una barra para ver los tenants" : "Click a bar to list the tenants"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-1.5" data-testid="admin-signups-bars">
        {months.map((m) => {
          const h = Math.max(4, Math.round((m.count / max) * 80));
          const isSel = selectedMonth === m.month;
          return (
            <button
              type="button"
              key={m.month}
              onClick={() => loadMonthTenants(m.month)}
              className={`flex flex-col items-center gap-1 rounded-sm px-0.5 py-1 transition-colors ${
                isSel ? "bg-[var(--brand-accent)]/10" : "hover:bg-[var(--surface-hover)]"
              }`}
              data-testid={`admin-signups-${m.month}`}
              title={`${m.month}: ${m.count}`}
            >
              <div className="text-[10px] font-mono-num text-[var(--text-secondary)]">{m.count}</div>
              <div className="w-full flex items-end" style={{ height: 80 }}>
                <div
                  className={`w-full rounded-sm transition-colors ${
                    isSel ? "bg-[var(--brand-accent)]" : "bg-[var(--brand-accent)]/70 hover:bg-[var(--brand-accent)]"
                  }`}
                  style={{ height: `${h}px` }}
                />
              </div>
              <div className={`text-[10px] ${isSel ? "text-[var(--text-primary)] font-semibold" : "text-[var(--text-muted)]"}`}>
                {m.month.slice(5)}/{m.month.slice(2, 4)}
              </div>
            </button>
          );
        })}
      </div>
      {Object.keys(referralTotals).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
            {lang === "es" ? "Cómo nos han conocido" : "How they heard about us"}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {Object.entries(referralTotals)
              .sort((a, b) => b[1] - a[1])
              .map(([src, count]) => (
                <div key={src} className="flex items-center gap-1.5">
                  <span className="text-[var(--text-muted)]">{labels[src] || src}:</span>
                  <span className="font-mono-num font-semibold">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Iter 82 — listado de tenants del mes seleccionado */}
      {selectedMonth && (
        <div className="mt-4 pt-4 border-t border-[var(--border-color)]" data-testid="admin-signups-tenant-list">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es"
                ? `Nuevos tenants · ${selectedMonth}`
                : `New tenants · ${selectedMonth}`}
              {tenants && (
                <span className="ml-2 font-mono-num text-[var(--text-primary)]">({tenants.length})</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => { setSelectedMonth(null); setTenants(null); }}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              data-testid="admin-signups-tenant-close"
            >
              ×
            </button>
          </div>
          {loadingTenants ? (
            <p className="text-xs text-[var(--text-muted)] py-3">
              {lang === "es" ? "Cargando…" : "Loading…"}
            </p>
          ) : tenants && tenants.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-3">
              {lang === "es" ? "Ningún alta este mes." : "No signups this month."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                    <th className="py-1.5 pr-3">{lang === "es" ? "Fecha" : "Date"}</th>
                    <th className="py-1.5 pr-3">Email</th>
                    <th className="py-1.5 pr-3">{lang === "es" ? "Nombre" : "Name"}</th>
                    <th className="py-1.5 pr-3">Plan</th>
                    <th className="py-1.5 pr-3">{lang === "es" ? "Origen" : "Source"}</th>
                    <th className="py-1.5 pr-3">Trial</th>
                  </tr>
                </thead>
                <tbody>
                  {(tenants || []).map((tn) => {
                    const dateShort = (tn.created_at || "").slice(0, 10);
                    const trialLabel = tn.trial_expired_at
                      ? (lang === "es" ? "Expirado" : "Expired")
                      : tn.is_trialing
                        ? (lang === "es" ? "Activo" : "Active")
                        : (lang === "es" ? "—" : "—");
                    const trialClass = tn.trial_expired_at
                      ? "text-[var(--status-negative)]"
                      : tn.is_trialing
                        ? "text-[var(--status-positive)]"
                        : "text-[var(--text-muted)]";
                    return (
                      <tr
                        key={tn.id}
                        className="border-b border-[var(--border-color)] last:border-b-0"
                        data-testid={`admin-signups-tenant-${tn.id}`}
                      >
                        <td className="py-1.5 pr-3 font-mono-num text-[var(--text-secondary)]">{dateShort}</td>
                        <td className="py-1.5 pr-3">{tn.email}</td>
                        <td className="py-1.5 pr-3 text-[var(--text-secondary)]">
                          {tn.company_name || tn.name || "—"}
                        </td>
                        <td className="py-1.5 pr-3">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[10px] uppercase font-semibold">
                            {tn.plan}
                          </span>
                        </td>
                        <td className="py-1.5 pr-3 text-[var(--text-secondary)]">
                          {labels[tn.referral_source] || tn.referral_source || "—"}
                        </td>
                        <td className={`py-1.5 pr-3 text-[11px] ${trialClass}`}>{trialLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatsCards({ stats, lang }) {  if (!stats) {
    return <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg h-24 animate-pulse" />;
  }
  const cards = [
    { label: lang === "es" ? "Usuarios" : "Users", value: stats.total_users, icon: UsersIcon, testid: "stat-users" },
    { label: lang === "es" ? "Facturas" : "Invoices", value: stats.total_invoices, icon: Receipt, testid: "stat-invoices" },
    { label: "MRR", value: `${stats.mrr.toFixed(2)} €`, icon: Euro, testid: "stat-mrr" },
    { label: "ARR", value: `${stats.arr.toFixed(2)} €`, icon: TrendingUp, testid: "stat-arr" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4" data-testid={c.testid}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{c.label}</p>
            <c.icon size={14} className="text-[var(--text-muted)]" />
          </div>
          <p className="font-display text-2xl font-bold tracking-tight font-mono-num">{c.value}</p>
        </div>
      ))}
      <div className="col-span-2 lg:col-span-4 bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4">
        <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">
          {lang === "es" ? "Distribución de planes" : "Plan distribution"}
        </p>
        <div className="flex items-center gap-4 flex-wrap text-sm">
          {Object.entries(stats.plan_counts || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5" data-testid={`plan-count-${k}`}>
              <span className="text-[var(--text-muted)]">{planLabel(k)}:</span>
              <span className="font-mono-num font-semibold">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ImpersonationBanner() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const hasImpersonator = typeof window !== "undefined" && localStorage.getItem("impersonator_token");
  if (!hasImpersonator) return null;

  const exit = async () => {
    const token = localStorage.getItem("impersonator_token");
    // iter 59 — close the audit row before restoring the master token so
    // the request goes out with the impersonation credentials (any auth
    // works — the endpoint is public and identifies the session by id).
    const auditId = localStorage.getItem("impersonation_audit_id");
    if (auditId) {
      try {
        await api.post("/admin/impersonate/end", { session_id: auditId });
      } catch {
        /* non-fatal, keep exiting even if the audit close fails */
      }
      localStorage.removeItem("impersonation_audit_id");
    }
    if (token) setToken(token);
    localStorage.removeItem("impersonator_token");
    window.location.href = "/admin";
  };

  return (
    <div
      className="bg-[var(--brand-accent)]/10 border-b border-[var(--brand-accent)]/30 px-4 py-2 text-xs flex items-center justify-between"
      data-testid="impersonation-banner"
    >
      <span className="text-[var(--brand-accent)] font-semibold flex items-center gap-1.5">
        <UserCog size={12} />
        {lang === "es" ? "Sesión impersonada por master" : "Impersonated session (master)"}
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={exit}
        data-testid="exit-impersonation"
        className="h-7 text-xs"
      >
        {lang === "es" ? "Volver a master" : "Back to master"}
      </Button>
    </div>
  );
}


function OcrFailuresPanel({ lang }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [details, setDetails] = useState(null); // currently-open invoice detail

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ocr-failures", { params: { limit: 20 } });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (inv) => {
    setBusyId(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/retry-ocr`);
      if (data?.ok) {
        toast.success(lang === "es" ? "OCR completada — datos actualizados" : "OCR succeeded — data updated");
      } else {
        toast.error(data?.error || (lang === "es" ? "OCR falló de nuevo" : "OCR failed again"));
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4" data-testid="admin-ocr-failures">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          <AlertTriangle size={18} className="text-[var(--status-warning)]" />
          {lang === "es" ? "Facturas con OCR fallida" : "OCR failures"}
          <span className="text-sm text-[var(--text-muted)] font-normal">({rows.length})</span>
        </h2>
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-ocr-failures-refresh">
          <RefreshCw size={14} className="mr-1.5" /> {lang === "es" ? "Actualizar" : "Refresh"}
        </Button>
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">
        {lang === "es"
          ? "Últimas 20 facturas cuya extracción OCR falló (timeout, parseo JSON, error del modelo, etc.). Puedes reintentar la extracción y los campos que ya estén rellenados manualmente se respetarán."
          : "Last 20 invoices whose OCR extraction failed. Retry will only fill empty fields."}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-ocr-failures-table">
          <thead className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Subida" : "Uploaded"}</th>
              <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Usuario" : "User"}</th>
              <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Concepto" : "Vendor"}</th>
              <th className="text-left py-2 px-2 font-medium">{lang === "es" ? "Error" : "Error"}</th>
              <th className="text-right py-2 px-2 font-medium">{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="text-center py-8 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-[var(--text-muted)]">
                {lang === "es" ? "Sin OCR fallidas. ✓" : "No OCR failures. ✓"}
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-[var(--border-color)] hover:bg-[var(--surface-hover)]" data-testid={`ocr-fail-row-${r.id}`}>
                <td className="py-2 px-2 font-mono-num text-xs text-[var(--text-secondary)]">
                  {r.created_at ? formatDateTimeShort(r.created_at) : "—"}
                </td>
                <td className="py-2 px-2 text-xs">{r.owner_email || "—"}</td>
                <td className="py-2 px-2">{r.vendor || <em className="text-[var(--text-muted)]">{lang === "es" ? "(sin proveedor)" : "(no vendor)"}</em>}</td>
                <td className="py-2 px-2 text-[var(--status-warning)] text-xs max-w-md truncate" title={r.ocr_error}>
                  {r.ocr_error || "—"}
                </td>
                <td className="py-2 px-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    {r.file_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDetails(r)}
                        title={lang === "es" ? "Ver detalle" : "View"}
                        data-testid={`ocr-fail-view-${r.id}`}
                      >
                        <Eye size={12} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => retry(r)}
                      disabled={busyId === r.id || !r.file_id}
                      title={r.file_id ? (lang === "es" ? "Reintentar OCR" : "Retry OCR") : (lang === "es" ? "Sin archivo adjunto" : "No attached file")}
                      data-testid={`ocr-fail-retry-${r.id}`}
                    >
                      <RotateCcw size={12} className={busyId === r.id ? "animate-spin" : ""} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {details && (
        <Dialog open onOpenChange={(o) => !o && setDetails(null)}>
          <DialogContent className="max-w-2xl" data-testid="ocr-fail-detail-dialog">
            <DialogHeader>
              <DialogTitle>{lang === "es" ? "Detalle OCR fallida" : "OCR failure detail"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p><strong>{lang === "es" ? "Usuario" : "User"}:</strong> {details.owner_email}</p>
              <p><strong>{lang === "es" ? "Concepto" : "Vendor"}:</strong> {details.vendor || "—"}</p>
              <p><strong>{lang === "es" ? "Subida" : "Uploaded"}:</strong> {details.created_at ? formatDateTimeShort(details.created_at) : "—"}</p>
              <p><strong>{lang === "es" ? "Importe" : "Amount"}:</strong> {details.amount ?? "—"} {details.currency}</p>
              <div className="rounded bg-[var(--status-warning-bg)] text-[var(--status-warning)] p-3 text-xs">
                <strong>{lang === "es" ? "Error" : "Error"}:</strong> {details.ocr_error}
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "Para corregirlo, puedes pulsar \"Reintentar OCR\" o entrar al usuario (impersonar) y editar la factura a mano."
                  : "To fix, click \"Retry OCR\" or impersonate the user and edit the invoice manually."}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetails(null)}>
                {lang === "es" ? "Cerrar" : "Close"}
              </Button>
              <Button
                onClick={() => { setDetails(null); retry(details); }}
                disabled={!details.file_id || busyId === details.id}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="ocr-fail-detail-retry"
              >
                <RotateCcw size={12} className="mr-1.5" />
                {lang === "es" ? "Reintentar OCR" : "Retry OCR"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// iter 59 — Master-only impersonation audit trail.
// Collapsed by default (avoids visual clutter on the Admin page). Fetches
// the latest 50 rows on expand and shows master/target/started/duration.
function ImpersonationAudit({ lang }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/impersonation-audit", { params: { limit: 50 } });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const fmtDuration = (startedAt, endedAt) => {
    if (!startedAt) return "—";
    if (!endedAt) return lang === "es" ? "en curso" : "in progress";
    const ms = new Date(endedAt) - new Date(startedAt);
    if (Number.isNaN(ms) || ms < 0) return "—";
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
    return `${(ms / 3_600_000).toFixed(1)}h`;
  };

  return (
    <details
      className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg"
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="impersonation-audit"
    >
      <summary className="px-4 py-3 cursor-pointer text-sm font-medium flex items-center justify-between select-none">
        <span className="flex items-center gap-2">
          <UserCog size={14} className="text-[var(--text-muted)]" />
          {lang === "es" ? "Auditoría de impersonaciones" : "Impersonation audit"}
          {open && total > 0 && (
            <span className="text-xs text-[var(--text-muted)] font-normal">
              ({total}{lang === "es" ? " registros" : " records"})
            </span>
          )}
        </span>
        <span className="text-xs text-[var(--text-muted)]">
          {lang === "es" ? "solo master" : "master only"}
        </span>
      </summary>
      <div className="border-t border-[var(--border-color)] p-4">
        {loading ? (
          <div className="text-xs text-[var(--text-muted)]">
            {lang === "es" ? "Cargando…" : "Loading…"}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-[var(--text-muted)]" data-testid="impersonation-audit-empty">
            {lang === "es"
              ? "Sin impersonaciones registradas."
              : "No impersonation sessions recorded yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="impersonation-audit-table">
              <thead>
                <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                  <th className="py-1.5 pr-3 font-medium">{lang === "es" ? "Master" : "Master"}</th>
                  <th className="py-1.5 pr-3 font-medium">{lang === "es" ? "Objetivo" : "Target"}</th>
                  <th className="py-1.5 pr-3 font-medium">{lang === "es" ? "Inicio" : "Started"}</th>
                  <th className="py-1.5 pr-3 font-medium">{lang === "es" ? "Duración" : "Duration"}</th>
                  <th className="py-1.5 font-medium">{lang === "es" ? "Estado" : "State"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 pr-3 font-mono">{r.master_email || "—"}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.target_email || "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--text-muted)]">
                      {r.started_at ? formatDateTimeShort(r.started_at) : "—"}
                    </td>
                    <td className="py-1.5 pr-3 font-mono-num">
                      {fmtDuration(r.started_at, r.ended_at)}
                    </td>
                    <td className="py-1.5">
                      {r.ended_at ? (
                        <span className="text-[var(--text-muted)]">
                          {lang === "es" ? "cerrada" : "closed"}
                        </span>
                      ) : (
                        <span className="text-[var(--brand-accent)] font-semibold">
                          {lang === "es" ? "activa" : "active"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  );
}
